import { useState } from 'react';
import { api } from '../lib/api';

interface LinkedInProfile {
  headline: string;
  about: string;
  education: string;
  experience: string[];
  skills_top5: string[];
  skills_full: string[];
  projects: string[];
}

export default function LinkedInPage() {
  const [profile, setProfile] = useState<LinkedInProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  async function generate() {
    setLoading(true);
    try {
      const data = await api.post<{ sections: LinkedInProfile }>('/api/profile/linkedin', {});
      setProfile(data.sections);
    } catch (err) {
      alert(`Generation failed: ${(err as Error).message}`);
    }
    setLoading(false);
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">LinkedIn Profile</h1>
      <p className="text-gray-600 mb-6">Generate copy-ready LinkedIn content from your knowledge base.</p>

      {!profile && (
        <button
          onClick={generate}
          disabled={loading}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Generating...' : 'Generate LinkedIn Profile'}
        </button>
      )}

      {loading && (
        <p className="mt-4 text-gray-500 animate-pulse">
          Crafting your profile sections from your knowledge base... this takes 10–20 seconds.
        </p>
      )}

      {profile && (
        <div className="space-y-6">
          <Section
            title="Headline"
            subtitle="120 chars max"
            content={profile.headline}
            onCopy={() => copyToClipboard(profile.headline, 'headline')}
            copied={copied === 'headline'}
          />

          <Section
            title="About"
            subtitle="2,600 chars max"
            content={profile.about}
            onCopy={() => copyToClipboard(profile.about, 'about')}
            copied={copied === 'about'}
          />

          <Section
            title="Education"
            content={profile.education}
            onCopy={() => copyToClipboard(profile.education, 'education')}
            copied={copied === 'education'}
          />

          <Section
            title="Experience"
            content={profile.experience.join('\n\n')}
            onCopy={() => copyToClipboard(profile.experience.join('\n\n'), 'experience')}
            copied={copied === 'experience'}
          />

          <Section
            title="Skills (Top 5)"
            content={profile.skills_top5.join(', ')}
            onCopy={() => copyToClipboard(profile.skills_top5.join(', '), 'skills')}
            copied={copied === 'skills'}
          />

          {profile.projects.length > 0 && (
            <Section
              title="Projects"
              content={profile.projects.join('\n\n')}
              onCopy={() => copyToClipboard(profile.projects.join('\n\n'), 'projects')}
              copied={copied === 'projects'}
            />
          )}

          <button
            onClick={generate}
            disabled={loading}
            className="mt-4 text-sm text-gray-600 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50"
          >
            Regenerate
          </button>
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  subtitle,
  content,
  onCopy,
  copied,
}: {
  title: string;
  subtitle?: string;
  content: string;
  onCopy: () => void;
  copied: boolean;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="font-medium text-gray-900">{title}</h2>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
        <button
          onClick={onCopy}
          className="text-sm px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 transition-colors"
          aria-label={`Copy ${title} to clipboard`}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-wrap">{content}</p>
    </div>
  );
}
